import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { generateSessionId } from '../utils/sessionUtils';
import { createSession } from '../services/sessionService';

const { FiPlus, FiTrash2, FiSave, FiArrowLeft } = FiIcons;

function CreateSession() {
  const navigate = useNavigate();
  const [clues, setClues] = useState([
    { clue: '', answer: '' },
    { clue: '', answer: '' },
    { clue: '', answer: '' }
  ]);
  const [completionMessage, setCompletionMessage] = useState('Glückwunsch! Du hast das Rätsel gelöst!');
  const [isCreating, setIsCreating] = useState(false);

  const addClue = () => {
    setClues([...clues, { clue: '', answer: '' }]);
  };

  const removeClue = (index) => {
    if (clues.length > 1) {
      setClues(clues.filter((_, i) => i !== index));
    }
  };

  const updateClue = (index, field, value) => {
    const updated = [...clues];
    updated[index][field] = value;
    setClues(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsCreating(true);

    const validClues = clues.filter(c => c.clue.trim() && c.answer.trim());
    
    if (validClues.length < 3) {
      alert('Bitte gib mindestens 3 vollständige Hinweis-Antwort-Paare ein.');
      setIsCreating(false);
      return;
    }

    try {
      const sessionId = generateSessionId();
      const sessionData = {
        id: sessionId,
        clues: validClues,
        completionMessage,
        createdAt: new Date().toISOString(),
        lastActivity: new Date().toISOString()
      };

      await createSession(sessionData);
      navigate(`/session/${sessionId}`);
    } catch (error) {
      console.error('Error creating session:', error);
      alert('Fehler beim Erstellen der Session. Bitte versuche es erneut.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-primary hover:opacity-80 mb-4 font-rubik"
          >
            <SafeIcon icon={FiArrowLeft} />
            Zurück zur Startseite
          </button>
          
          <h1 className="text-3xl font-bold text-primary mb-2 font-rubik">
            Neues Kreuzworträtsel erstellen
          </h1>
          <p className="text-primary font-rubik">
            Gib deine Hinweise und Lösungswörter ein. Das Gitter wird automatisch generiert.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="mb-6">
            <label className="block text-sm font-medium text-primary mb-2 font-rubik">
              Schlussnachricht
            </label>
            <input
              type="text"
              value={completionMessage}
              onChange={(e) => setCompletionMessage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-primary font-rubik"
              placeholder="Nachricht bei vollständiger Lösung"
            />
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-primary font-rubik">
                Hinweise und Lösungswörter
              </h2>
              <button
                type="button"
                onClick={addClue}
                className="flex items-center gap-2 px-4 py-2 bg-button text-white rounded-md hover:bg-button-hover transition-colors font-rubik"
              >
                <SafeIcon icon={FiPlus} />
                Hinweis hinzufügen
              </button>
            </div>

            <div className="space-y-4">
              {clues.map((clue, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="grid md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <label className="block text-sm font-medium text-primary mb-1 font-rubik">
                      Hinweis {index + 1}
                    </label>
                    <input
                      type="text"
                      value={clue.clue}
                      onChange={(e) => updateClue(index, 'clue', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-primary font-rubik"
                      placeholder="z.B. Hauptstadt von Deutschland"
                      required
                    />
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-primary mb-1 font-rubik">
                        Lösungswort
                      </label>
                      <input
                        type="text"
                        value={clue.answer}
                        onChange={(e) => updateClue(index, 'answer', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-primary font-rubik"
                        placeholder="z.B. BERLIN"
                        required
                      />
                    </div>
                    {clues.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeClue(index)}
                        className="mt-6 p-2 text-red-600 hover:bg-red-50 rounded-md"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="px-6 py-2 border border-gray-300 text-primary rounded-md hover:bg-gray-50 transition-colors font-rubik"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isCreating}
              className="flex items-center gap-2 px-6 py-2 bg-button text-white rounded-md hover:bg-button-hover transition-colors disabled:opacity-50 font-rubik"
            >
              <SafeIcon icon={FiSave} />
              {isCreating ? 'Erstelle...' : 'Rätsel erstellen'}
            </button>
          </div>
        </motion.form>
      </div>
    </div>
  );
}

export default CreateSession;